import React, { useState } from 'react'
import { useHRStore } from '../store/hrStore'
import { CheckCircle, DollarSign, Heart, Users, Plus } from 'lucide-react'

export default function BenefitsPage() {
  const { benefits, employees, employeeBenefits, addBenefit } = useHRStore()
  const [showAddModal, setShowAddModal] = useState(false)
  const [benefitForm, setBenefitForm] = useState({
    name: '',
    type: 'Medical',
    description: '',
    employees: 0,
    premium: 0,
  })

  const handleSaveBenefit = () => {
    if (!benefitForm.name) {
      alert('Please enter benefit name')
      return
    }
    addBenefit(benefitForm)
    setShowAddModal(false)
    setBenefitForm({
      name: '',
      type: 'Medical',
      description: '',
      employees: 0,
      premium: 0,
    })
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Medical':
        return '🏥'
      case 'Dental':
        return '🦷'
      case 'Retirement':
        return '🏦'
      case 'Insurance':
        return '🛡️'
      default:
        return '✓'
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'Medical':
        return 'from-red-600 to-red-500'
      case 'Dental':
        return 'from-blue-600 to-blue-500'
      case 'Retirement':
        return 'from-green-600 to-green-500'
      case 'Insurance':
        return 'from-yellow-600 to-yellow-500'
      default:
        return 'from-slate-600 to-slate-500'
    }
  }

  const totalBenefitsCost = benefits.reduce((sum, b) => sum + b.premium * b.employees, 0)
  const coverageRate = ((benefits.reduce((sum, b) => sum + b.employees, 0) / (employees.length * 4)) * 100).toFixed(0)

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Benefits Management</h1>
          <p className="text-slate-400">Manage health insurance, retirement plans, and employee benefits</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Active Benefits</h3>
              <CheckCircle className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-3xl font-bold text-white">{benefits.length}</div>
            <div className="text-sm text-slate-400">Available plans</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6 hover:border-green-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Coverage Rate</h3>
              <Heart className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-3xl font-bold text-white">{coverageRate}%</div>
            <div className="text-sm text-slate-400">Employee enrollment</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-yellow-500/20 p-6 hover:border-yellow-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Total Employees</h3>
              <Users className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="text-3xl font-bold text-white">{benefits.reduce((sum, b) => sum + b.employees, 0)}</div>
            <div className="text-sm text-slate-400">Enrolled</div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-cyan-500/20 p-6 hover:border-cyan-400/40 transition-all">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-400 text-sm font-medium">Monthly Cost</h3>
              <DollarSign className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-white">${(totalBenefitsCost / 1000).toFixed(0)}K</div>
            <div className="text-sm text-slate-400">Total benefits cost</div>
          </div>
        </div>

        {/* Benefits Catalog */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-purple-500/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Heart className="w-6 h-6 text-purple-400" />
              Benefits Catalog
            </h2>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              Add Benefit
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit) => (
              <div key={benefit.id} className="bg-slate-700/50 rounded-lg p-6 border border-blue-500/20 hover:border-blue-400/40 transition-all">
                <div className={`bg-gradient-to-br ${getTypeColor(benefit.type)} p-3 rounded-lg w-fit mb-4`}>
                  <span className="text-2xl">{getTypeIcon(benefit.type)}</span>
                </div>

                <h3 className="text-white font-bold text-lg mb-2">{benefit.name}</h3>
                <p className="text-slate-400 text-sm mb-4">{benefit.description}</p>

                <div className="space-y-2 mb-4 border-t border-blue-500/20 pt-4">
                  <div className="flex justify-between">
                    <span className="text-slate-400 text-sm">Enrolled Employees</span>
                    <span className="text-cyan-400 font-bold">{benefit.employees}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400 text-sm">Monthly Premium</span>
                    <span className="text-green-400 font-bold">${benefit.premium}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400 text-sm">Type</span>
                    <span className="text-blue-400 font-bold">{benefit.type}</span>
                  </div>
                </div>

                <div className="w-full bg-slate-600 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{
                      width: `${Math.min((benefit.employees / employees.length) * 100, 100)}%`,
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add Benefit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-xl border border-purple-500/20 max-w-md w-full mx-4 p-6">
              <h2 className="text-2xl font-bold text-white mb-4">Add Benefit</h2>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Benefit Name</label>
                  <input
                    type="text"
                    value={benefitForm.name}
                    onChange={(e) => setBenefitForm({ ...benefitForm, name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                    placeholder="e.g., Health Insurance"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Type</label>
                  <select
                    value={benefitForm.type}
                    onChange={(e) => setBenefitForm({ ...benefitForm, type: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                  >
                    <option value="Medical">Medical</option>
                    <option value="Dental">Dental</option>
                    <option value="Retirement">Retirement</option>
                    <option value="Insurance">Insurance</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Description</label>
                  <textarea
                    value={benefitForm.description}
                    onChange={(e) => setBenefitForm({ ...benefitForm, description: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                    placeholder="Benefit description"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Employees Enrolled</label>
                  <input
                    type="number"
                    value={benefitForm.employees}
                    onChange={(e) => setBenefitForm({ ...benefitForm, employees: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-400 text-sm mb-2">Monthly Premium per Employee</label>
                  <input
                    type="number"
                    value={benefitForm.premium}
                    onChange={(e) => setBenefitForm({ ...benefitForm, premium: parseInt(e.target.value) || 0 })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-purple-500 focus:outline-none"
                    placeholder="$0"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBenefit}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
